/* eslint-disable max-lines */
import { Store } from '../store/types';
import { LoyaltyAccountEvents, LoyaltyPointsEvents, LoyaltyRankEvents } from '../actions/loyalty';
import {
  FetchLoyaltyAccountDTO,
  FetchLoyaltyAccountFailureAction,
  FetchLoyaltyPointsDTO,
  FetchLoyaltyPointsFailureAction,
  FetchLoyaltyRankDTO,
} from '../types/loyalty';
import { getLoyaltyAccountRequest } from 'api/get-loyalty-account-request';
import { ErrorWithStatus } from '../api/error-with-status';
import { LOYATY_ACCOUNT_DATA_EXPIRY } from '../common/constants/local-storage-constants';
import { getLoyaltyRankRequest } from '../api/get-loyalty-rank-request';
import { getLoyaltyPointsRequest } from '../api/get-loyalty-points-request';
import { getCustomerId } from '../selectors';
import { getLoyaltyAccountDataKey } from '../common/utils/saved-loyalty-account-data';

export const loyaltyReducer = (store: Store) => {
  store.on('@init', (state) => ({
    ...state,
    loyalty: {
      fetchLoyaltyAccount: {
        loading: false,
        error: null,
        fetched: false,
      },
      fetchLoyaltyPoints: {
        loading: false,
        error: null,
        fetched: false,
      },
      fetchLoyaltyRank: {
        loading: false,
        error: null,
        fetched: false,
      },
      loyaltyAccountIdentifier: null,
      loyaltyTypePRO: null,
      points: null,
      rankLabel: null,
    },
  }));

  store.on(LoyaltyAccountEvents.restoreAccount, (state, { loyaltyAccountIdentifier, loyaltyTypePRO }) => ({
    ...state,
    loyalty: {
      ...state.loyalty,
      fetchLoyaltyAccount: {
        ...state.loyalty.fetchLoyaltyAccount,
        loading: false,
        fetched: true,
        error: null,
      },
      loyaltyAccountIdentifier,
      loyaltyTypePRO,
    },
  }));

  store.on(LoyaltyAccountEvents.init, () => {
    const { orchestratorHost, useCredentials, apiKey, requestID, model } = store.get();
    const regionId = model?.properties.regionId;

    if (regionId) {
      store.dispatch(LoyaltyAccountEvents.request);

      getLoyaltyAccountRequest(
        {
          regionId,
        },
        {
          orchestratorHost,
          useCredentials,
          apiKey,
          requestID,
        },
      )
        .then((result: FetchLoyaltyAccountDTO) => {
          store.dispatch(LoyaltyAccountEvents.success, result);
        })
        .catch((e) => {
          const error = e as FetchLoyaltyAccountFailureAction | ErrorWithStatus;

          if (e?.status == 404) {
            store.dispatch(LoyaltyAccountEvents.success, {
              loyaltyAccountIdentifier: null,
              loyaltyTypePRO: false,
            });

            return;
          }

          store.dispatch(
            LoyaltyAccountEvents.failure,
            error || {
              message: 'Something went wrong',
            },
          );
        });
    }
  });

  store.on(LoyaltyAccountEvents.request, (state) => ({
    ...state,
    loyalty: {
      ...state.loyalty,
      fetchLoyaltyAccount: {
        ...state.loyalty.fetchLoyaltyAccount,
        loading: true,
        fetched: false,
        error: null,
      },
    },
  }));

  store.on(LoyaltyAccountEvents.success, (state, data) => {
    const { cookies } = store.get();
    const customerNumber = getCustomerId(cookies);

    localStorage.setItem(
      getLoyaltyAccountDataKey(customerNumber),
      JSON.stringify({
        loyaltyAccountIdentifier: data.loyaltyAccountIdentifier,
        loyaltyTypePRO: data.loyaltyTypePRO,
        expiry: new Date().getTime() + LOYATY_ACCOUNT_DATA_EXPIRY,
      }),
    );

    return {
      ...state,
      loyalty: {
        ...state.loyalty,
        fetchLoyaltyAccount: {
          ...state.loyalty.fetchLoyaltyAccount,
          loading: false,
          fetched: true,
        },
        loyaltyAccountIdentifier: data.loyaltyAccountIdentifier,
        loyaltyTypePRO: data.loyaltyTypePRO,
      },
    };
  });

  store.on(LoyaltyAccountEvents.failure, (state, error) => ({
    ...state,
    loyalty: {
      ...state.loyalty,
      fetchLoyaltyAccount: {
        ...state.loyalty.fetchLoyaltyAccount,
        loading: false,
        fetched: true,
        error,
      },
      loyaltyAccountIdentifier: null,
      loyaltyTypePRO: null,
    },
  }));

  store.on(LoyaltyPointsEvents.init, () => {
    const { orchestratorHost, useCredentials, apiKey, requestID, model, loyalty } = store.get();
    const regionId = model?.properties.regionId;

    if (regionId && loyalty.loyaltyAccountIdentifier) {
      store.dispatch(LoyaltyPointsEvents.request);

      getLoyaltyPointsRequest(
        {
          loyaltyAccountIdentifier: loyalty.loyaltyAccountIdentifier,
          regionId,
        },
        {
          orchestratorHost,
          useCredentials,
          apiKey,
          requestID,
        },
      )
        .then((result: FetchLoyaltyPointsDTO) => {
          store.dispatch(LoyaltyPointsEvents.success, {
            points: result.points,
          });
        })
        .catch((e) => {
          const error = e as FetchLoyaltyPointsFailureAction | ErrorWithStatus;

          store.dispatch(
            LoyaltyPointsEvents.failure,
            error || {
              message: 'Something went wrong',
            },
          );
        });
    }
  });

  store.on(LoyaltyPointsEvents.request, (state) => ({
    ...state,
    loyalty: {
      ...state.loyalty,
      fetchLoyaltyPoints: {
        ...state.loyalty.fetchLoyaltyPoints,
        loading: true,
        fetched: false,
        error: null,
      },
    },
  }));

  store.on(LoyaltyPointsEvents.success, (state, data) => {
    return {
      ...state,
      loyalty: {
        ...state.loyalty,
        fetchLoyaltyPoints: {
          ...state.loyalty.fetchLoyaltyPoints,
          loading: false,
          fetched: true,
        },
        points: data.points,
      },
    };
  });

  store.on(LoyaltyPointsEvents.failure, (state, error) => ({
    ...state,
    loyalty: {
      ...state.loyalty,
      fetchLoyaltyPoints: {
        ...state.loyalty.fetchLoyaltyPoints,
        loading: false,
        fetched: true,
        error,
      },
      points: null,
    },
  }));

  store.on(LoyaltyRankEvents.init, () => {
    const { orchestratorHost, useCredentials, apiKey, requestID, model, loyalty } = store.get();
    const regionId = model?.properties.regionId;

    if (regionId && loyalty.loyaltyAccountIdentifier) {
      store.dispatch(LoyaltyRankEvents.request);

      getLoyaltyRankRequest(
        {
          loyaltyAccountIdentifier: loyalty.loyaltyAccountIdentifier,
          regionId,
        },
        {
          orchestratorHost,
          useCredentials,
          apiKey,
          requestID,
        },
      )
        .then((result: FetchLoyaltyRankDTO) => {
          store.dispatch(LoyaltyRankEvents.success, {
            rankLabel: result.label,
          });
        })
        .catch((e) => {
          const error = e as FetchLoyaltyAccountFailureAction | ErrorWithStatus;

          store.dispatch(
            LoyaltyRankEvents.failure,
            error || {
              message: 'Something went wrong',
            },
          );
        });
    }
  });

  store.on(LoyaltyRankEvents.request, (state) => ({
    ...state,
    loyalty: {
      ...state.loyalty,
      fetchLoyaltyRank: {
        ...state.loyalty.fetchLoyaltyRank,
        loading: true,
        fetched: false,
        error: null,
      },
    },
  }));

  store.on(LoyaltyRankEvents.success, (state, data) => {
    return {
      ...state,
      loyalty: {
        ...state.loyalty,
        fetchLoyaltyRank: {
          ...state.loyalty.fetchLoyaltyRank,
          loading: false,
          fetched: true,
        },
        rankLabel: data.rankLabel,
      },
    };
  });

  store.on(LoyaltyRankEvents.failure, (state, error) => ({
    ...state,
    loyalty: {
      ...state.loyalty,
      fetchLoyaltyRank: {
        ...state.loyalty.fetchLoyaltyRank,
        loading: false,
        fetched: true,
        error,
      },
      rankLabel: null,
    },
  }));
};

export type LoyaltyStore = {
  loyalty: {
    fetchLoyaltyAccount: {
      loading: boolean;
      error: ErrorWithStatus | null;
      fetched: boolean;
    };
    fetchLoyaltyPoints: {
      loading: boolean;
      error: ErrorWithStatus | null;
      fetched: boolean;
    };
    fetchLoyaltyRank: {
      loading: boolean;
      error: ErrorWithStatus | null;
      fetched: boolean;
    };
    loyaltyAccountIdentifier: string | null;
    loyaltyTypePRO: boolean | null;
    points: number | null;
    rankLabel: string | null;
  };
};
